/**
 * Navigation Configuration
 * Feature: 012-desktop-mobile-shells
 * 
 * Defines navigation items for desktop sidebar and mobile bottom tabs.
 * This is the single source of truth for navigation destinations.
 */

import { NavItem } from '../domain/shell';

/**
 * Static configuration of all navigation items.
 * Both desktop sidebar and mobile bottom navigation consume this configuration.
 * 
 * @remarks
 * - Maximum 5 items can have `showInMobileTab: true` per Material Design guidelines
 * - Items with `requiredFeature` will only show if the feature flag is enabled
 * - Translation keys must exist in all 18 language files
 */
export const NAVIGATION_CONFIG: NavItem[] = [
  // Primary destinations (both shells)
  {
    route: '/patients',
    labelKey: 'NAV.PATIENTS',
    labelKeySingular: 'NAV.PATIENT',
    icon: 'favorite',
    showInMobileTab: true,
    showInDesktopSidebar: true
  },
  {
    route: '/surveys',
    labelKey: 'NAV.SURVEYS',
    icon: 'assignment',
    // INTENTIONAL PRODUCT DECISION (Feature 020-pulse-tracker, revised): Surveys
    // is dormant/off-by-default with only pilot users, and the placeholder entry
    // added more confusion than value, so it is now hidden from BOTH shells.
    // Its former mobile bottom-bar slot is taken by Pulse (mobile bar =
    // Patients · Pulse · Messages · Profile); the desktop sidebar entry was
    // removed too.
    //
    // Consequence, accepted deliberately: with both flags false there is NO
    // browsable route to Surveys anywhere. The `/surveys` route still resolves,
    // so deep links and notifications keep working — that is now the only way
    // in. Do NOT "fix" this by flipping either flag back to true; re-enabling
    // browse nav is a product decision, not a nav-config tidy-up.
    showInMobileTab: false,
    showInDesktopSidebar: false,
    requiredFeature: 'surveys',
    showWhenDisabled: true  // Show nav, route to placeholder when disabled
  },
  {
    route: '/care-profile',
    labelKey: 'NAV.CARE_PROFILE',
    icon: 'emergency',
    // INTENTIONAL PRODUCT DECISION (Feature 019 / Message Center, revised):
    // Care+ is deprioritized and now hidden from BOTH shells. It was already
    // absent from the mobile bottom bar — its slot is taken by Messages (mobile
    // bar = Patients · Pulse · Messages · Profile) — and the desktop sidebar
    // entry has been removed as well because it was a placeholder.
    //
    // Consequence, accepted deliberately: with both flags false there is NO
    // browsable route to Care+ anywhere. The `/care-profile` route still
    // resolves, so deep links and notifications keep working — that is now the
    // only way in. Do NOT "fix" this by flipping either flag back to true; the
    // omission is deliberate on both shells.
    showInMobileTab: false,
    showInDesktopSidebar: false,
    requiredFeature: 'care_profile',
    showWhenDisabled: true  // Show nav, route to placeholder when disabled
  },
  // Pulse — caregiver personal tracker (Feature 020-pulse-tracker). Like Messages,
  // the nav item is ALWAYS shown; the `pulse` flag gates CONTENT only (route renders
  // FeaturePlaceholder when off), not nav visibility. Pulse is a primary caregiver
  // destination, so it takes the mobile bottom-tab slot Surveys used to occupy
  // (mobile bar = Patients · Pulse · Messages · Profile = 4). Care+ and Surveys
  // are hidden from both shells and are unaffected.
  {
    route: '/pulse',
    labelKey: 'NAV.PULSE',
    icon: 'monitor_heart',
    showInMobileTab: true,
    showInDesktopSidebar: true,
    requiredFeature: 'pulse',
    showWhenDisabled: true  // Show nav, route to placeholder when disabled (mirrors Messages)
  },
  // Messages — always shown like Care+; the `messages` flag gates CONTENT only
  // (route renders FeaturePlaceholder when off), not nav visibility. On mobile
  // Messages occupies Care+'s former bottom-bar slot (Patients · Pulse · Messages · Profile).
  {
    route: '/messages',
    labelKey: 'NAV.MESSAGES',
    icon: 'mail',
    showInMobileTab: true,
    showInDesktopSidebar: true,
    requiredFeature: 'messages',
    showWhenDisabled: true,  // Show nav, route to placeholder when disabled (mirrors Care+)
    badgeKey: 'messages-unread'  // Unread-count badge (rendered in Phase 5)
  },
  // Profile (now visible in both shells - Feature 013-shell-navigation)
  {
    route: '/profile',
    labelKey: 'NAV.PROFILE',
    icon: 'person',
    showInMobileTab: true,
    showInDesktopSidebar: true  // CHANGED: now visible in desktop sidebar
  },
  {
    route: '/notifications',
    labelKey: 'NAV.NOTIFICATIONS',
    icon: 'notifications',
    showInMobileTab: false,
    showInDesktopSidebar: true,
    requiredFeature: 'push_notification',
    showWhenDisabled: false  // Hide if disabled (existing behavior)
  }
];

/**
 * Get navigation items for mobile bottom tabs.
 * Filters items where `showInMobileTab` is true.
 * 
 * @returns Array of NavItem for mobile bottom navigation (max 5 items)
 */
export function getMobileTabItems(): NavItem[] {
  return NAVIGATION_CONFIG.filter(item => item.showInMobileTab);
}

/**
 * Get navigation items for desktop sidebar.
 * Filters items where `showInDesktopSidebar` is true.
 * 
 * @returns Array of NavItem for desktop sidebar navigation
 */
export function getDesktopSidebarItems(): NavItem[] {
  return NAVIGATION_CONFIG.filter(item => item.showInDesktopSidebar);
}
